import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle,
  Edit3,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Info,
  LoaderCircle,
  MessageCircle,
  Printer,
  RefreshCw,
  Trash2,
  X
} from 'lucide-react';
import { Colonia } from '../../types/colonia';
import {
  criarFaturaInscricao,
  deleteAdminInscricoes,
  getAvailableAdminPeriods,
  getCurrentAdminPeriod,
  getInscricaoYear,
  obterFaturaInscricao,
  regenerarFaturaInscricao,
  useAdminInscricoesData,
  verifyAdminPayments
} from './adminData';
import { compareTurmas } from '../formularioParts/utils';
import { CompraEvento, fetchComprasEvento } from '../eventoAdicionalData';
import AdminTopbar from './AdminTopbar';
import { exportarInscricoesExcel } from './excelExport';
import { sendRegistrationConfirmation } from './messagingApi';
import RegistrationEditModal from './RegistrationEditModal';
import './AdminPanel.css';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const VALOR_ASSOCIADO_UBA = 150;
const VALOR_NAO_ASSOCIADO_UBA = 180;

const normalizeText = (value = '') =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const formatBoolean = (value?: boolean) => (value ? 'Sim' : 'Não');

const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <div className="registration-info-row">
    <span>{label}</span>
    <strong>{value || '-'}</strong>
  </div>
);

const ModalPhoto = ({ label, src }: { label: string; src?: string }) => (
  <div className="registration-modal-photo">
    {src ? <img src={src} alt={label} /> : <div>{label.slice(0, 2)}</div>}
    <span>{label}</span>
  </div>
);

const RegistrationDetailsModal = ({ inscricao, onClose }: { inscricao: Colonia; onClose: () => void }) => {
  const [faturaBusy, setFaturaBusy] = useState<'abrir' | 'regenerar' | 'criar' | null>(null);
  const [faturaErro, setFaturaErro] = useState('');
  const [faturaInfo, setFaturaInfo] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState(inscricao.invoiceUrl || '');
  const [cpfFatura, setCpfFatura] = useState('');
  const [faturaCriadaId, setFaturaCriadaId] = useState('');
  const [associadoFatura, setAssociadoFatura] = useState<boolean | null>(inscricao.associadoUba ?? null);

  const pendente = inscricao.status === 'pendente' && !inscricao.pago && !inscricao.gratuito;
  const temFatura = Boolean(
    faturaCriadaId || (inscricao.asaasPaymentId && !['GRATUITO', 'GRATUIDADE_CUPOM'].includes(inscricao.asaasPaymentId))
  );
  const semValorDefinido = !inscricao.valorTotal || inscricao.valorTotal <= 0;

  const criarFatura = async () => {
    setFaturaErro('');
    setFaturaInfo('');
    const cpfLimpo = cpfFatura.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) {
      setFaturaErro('Informe o CPF do responsável (11 dígitos) para criar a fatura.');
      return;
    }
    if (semValorDefinido && associadoFatura === null) {
      setFaturaErro('Selecione se o responsável é associado ou não associado para definir o valor da fatura.');
      return;
    }
    setFaturaBusy('criar');
    try {
      const fatura = await criarFaturaInscricao(
        inscricao.id!,
        cpfLimpo,
        semValorDefinido ? { associado: associadoFatura! } : undefined
      );
      setInvoiceUrl(fatura.invoiceUrl);
      setFaturaCriadaId(fatura.id);
      setFaturaInfo(`Fatura criada (${fatura.id}). Envie o link para o responsável pagar.`);
    } catch (erro) {
      setFaturaErro(erro instanceof Error ? erro.message : 'Não foi possível criar a fatura.');
    } finally {
      setFaturaBusy(null);
    }
  };

  const abrirFatura = async () => {
    setFaturaErro('');
    // A aba precisa abrir de forma síncrona para o navegador não bloquear o pop-up.
    const janela = window.open('about:blank', '_blank');
    if (invoiceUrl) {
      if (janela) janela.location.href = invoiceUrl;
      return;
    }
    setFaturaBusy('abrir');
    try {
      const fatura = await obterFaturaInscricao(inscricao.id!);
      setInvoiceUrl(fatura.invoiceUrl);
      if (janela && fatura.invoiceUrl) janela.location.href = fatura.invoiceUrl;
      else if (janela) janela.close();
      if (!fatura.invoiceUrl) setFaturaErro('O Asaas não retornou o link da fatura.');
    } catch (erro) {
      janela?.close();
      setFaturaErro(erro instanceof Error ? erro.message : 'Não foi possível localizar a fatura.');
    } finally {
      setFaturaBusy(null);
    }
  };

  const regenerarFatura = async () => {
    setFaturaErro('');
    setFaturaInfo('');
    const confirmado = window.confirm(
      `Excluir a fatura atual de ${inscricao.nomeCompleto} no Asaas e gerar uma nova idêntica?\n\nO link antigo deixa de funcionar.`
    );
    if (!confirmado) return;
    setFaturaBusy('regenerar');
    try {
      const { fatura, aviso } = await regenerarFaturaInscricao(inscricao.id!);
      setInvoiceUrl(fatura.invoiceUrl);
      setFaturaInfo(aviso || `Nova fatura gerada (${fatura.id}). O link antigo foi cancelado.`);
    } catch (erro) {
      setFaturaErro(erro instanceof Error ? erro.message : 'Não foi possível regenerar a fatura.');
    } finally {
      setFaturaBusy(null);
    }
  };

  return (
  <div className="registration-modal-backdrop" role="presentation" onClick={onClose}>
    <div className="registration-details-modal" role="dialog" aria-modal="true" aria-labelledby="registration-details-title" onClick={(event) => event.stopPropagation()}>
      <div className="registration-details-header">
        <div>
          <h3 id="registration-details-title">Ficha da inscrição</h3>
          <p>{inscricao.nomeCompleto}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Fechar"><X size={20} /></button>
      </div>

      <div className="registration-details-body">
        <div className="registration-photo-strip">
          <ModalPhoto label="Criança" src={inscricao.fotoUrl} />
          <ModalPhoto label="Retirada" src={inscricao.fotoUrlRetirada} />
          <ModalPhoto label="Assinatura" src={inscricao.assinaturaUrl} />
        </div>

        <section>
          <h4>Criança</h4>
          <InfoRow label="Nascimento" value={inscricao.dataNascimento} />
          <InfoRow label="Idade" value={`${inscricao.idade} anos`} />
          <InfoRow label="Turma" value={inscricao.turma} />
          <InfoRow label="Camisa" value={inscricao.tamanhoCamisa} />
          <InfoRow label="Associado" value={inscricao.associadoUba ? inscricao.codigoAssociado || 'Sim' : 'Não'} />
        </section>

        <section>
          <h4>Contato</h4>
          <InfoRow label="Responsável" value={inscricao.nomeMae} />
          <InfoRow label="WhatsApp" value={inscricao.telefoneMae} />
          <InfoRow label="Pai" value={inscricao.nomePai} />
          <InfoRow label="Tel. pai" value={inscricao.telefonePai} />
        </section>

        <section>
          <h4>Endereço</h4>
          <InfoRow label="CEP" value={inscricao.cep} />
          <InfoRow label="Rua" value={`${inscricao.endereco}, ${inscricao.numero}${inscricao.complemento ? ` - ${inscricao.complemento}` : ''}`} />
          <InfoRow label="Bairro" value={`${inscricao.bairro}`} />
          <InfoRow label="Cidade/UF" value={`${inscricao.cidade}/${inscricao.uf}`} />
        </section>

        <section>
          <h4>Retirada</h4>
          <InfoRow label="Principal" value={inscricao.nomeRetirada} />
          <InfoRow label="Parentesco" value={inscricao.parentescoRetirada} />
          {inscricao.pessoasRetirada?.map((pessoa, index) => (
            <InfoRow key={pessoa.id || index} label={`Autorizado ${index + 1}`} value={`${pessoa.nome} - ${pessoa.parentesco}`} />
          ))}
        </section>

        <section>
          <h4>Informações</h4>
          <InfoRow label="Sabe nadar" value={formatBoolean(inscricao.sabeNadar)} />
          <InfoRow label="Piscina" value={inscricao.autorizacaoPiscina} />
          <InfoRow label="Alergia" value={inscricao.possuiAlergia ? inscricao.alergias || 'Sim' : 'Não'} />
          <InfoRow label="Doença" value={inscricao.possuiDoenca ? inscricao.doencas || 'Sim' : 'Não'} />
          <InfoRow label="Medic." value={inscricao.tomaMedicamento ? inscricao.medicamentos || 'Sim' : 'Não'} />
        </section>

        <section>
          <h4>Pagamento</h4>
          <InfoRow label="Status" value={inscricao.status} />
          <InfoRow label="Pago" value={formatBoolean(inscricao.pago)} />
          <InfoRow label="Gratuito" value={formatBoolean(inscricao.gratuito)} />
          <InfoRow label="Valor" value={formatCurrency(inscricao.valorTotal)} />
          <InfoRow label="Método" value={inscricao.paymentMethod} />
          <InfoRow label="Asaas" value={inscricao.asaasPaymentId} />
          <InfoRow label="Parcelas" value={inscricao.installmentCount ? `${inscricao.installmentCount}x de ${formatCurrency(inscricao.installmentValue || 0)}` : undefined} />
          <InfoRow label="Fatura" value={invoiceUrl ? <a href={invoiceUrl} target="_blank" rel="noreferrer">Abrir fatura</a> : '-'} />

          {pendente && temFatura && (
            <div className="registration-invoice-actions">
              <button type="button" className="legacy-action-button green" onClick={abrirFatura} disabled={faturaBusy !== null}>
                <ExternalLink size={15} />
                {faturaBusy === 'abrir' ? 'Localizando...' : 'Acessar fatura'}
              </button>
              <button type="button" className="legacy-action-button orange" onClick={regenerarFatura} disabled={faturaBusy !== null}>
                <RefreshCw size={15} />
                {faturaBusy === 'regenerar' ? 'Gerando nova fatura...' : 'Excluir e gerar nova'}
              </button>
            </div>
          )}
          {pendente && !temFatura && (
            <div className="registration-invoice-create">
              {semValorDefinido ? (
                <>
                  <p className="registration-invoice-note">
                    Esta inscrição não tem valor definido. Selecione se o responsável é associado ou não associado do UBA
                    Clube para definir o valor da cobrança.
                  </p>
                  <div className="registration-invoice-tipo">
                    <button
                      type="button"
                      className={`legacy-action-button ${associadoFatura === true ? 'green' : 'outline'}`}
                      onClick={() => setAssociadoFatura(true)}
                    >
                      Associado – {formatCurrency(VALOR_ASSOCIADO_UBA)}
                    </button>
                    <button
                      type="button"
                      className={`legacy-action-button ${associadoFatura === false ? 'green' : 'outline'}`}
                      onClick={() => setAssociadoFatura(false)}
                    >
                      Não associado – {formatCurrency(VALOR_NAO_ASSOCIADO_UBA)}
                    </button>
                  </div>
                </>
              ) : (
                <p className="registration-invoice-note">
                  Esta inscrição pendente não tem fatura no Asaas. Informe o CPF do responsável para criar uma cobrança de{' '}
                  <strong>{formatCurrency(inscricao.valorTotal)}</strong>.
                </p>
              )}
              <div className="registration-invoice-create-row">
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="CPF do responsável"
                  maxLength={14}
                  value={cpfFatura}
                  onChange={(event) => setCpfFatura(event.target.value)}
                />
                <button type="button" className="legacy-action-button green" onClick={criarFatura} disabled={faturaBusy !== null}>
                  <FileText size={15} />
                  {faturaBusy === 'criar' ? 'Criando fatura...' : 'Criar fatura'}
                </button>
              </div>
            </div>
          )}
          {faturaErro && <p className="registration-invoice-error">{faturaErro}</p>}
          {faturaInfo && <p className="registration-invoice-info">{faturaInfo}</p>}
        </section>
      </div>
    </div>
  </div>
  );
};

const ChildPhoto: React.FC<{ inscricao: Colonia }> = ({ inscricao }) => {
  const [failed, setFailed] = useState(false);
  const initials = inscricao.nomeCompleto
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('');

  if (!inscricao.fotoUrl || failed) {
    return <div className="legacy-photo-placeholder">{initials}</div>;
  }

  return (
    <img
      className="legacy-photo"
      src={inscricao.fotoUrl}
      alt={`Foto de ${inscricao.nomeCompleto}`}
      onError={() => setFailed(true)}
    />
  );
};

const GerenciamentoColonia: React.FC = () => {
  const { inscricoes, loading, error } = useAdminInscricoesData();
  const [selectedYear, setSelectedYear] = useState(getCurrentAdminPeriod);
  const [searchTerm, setSearchTerm] = useState('');
  const [operationTerm, setOperationTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [turmaFilter, setTurmaFilter] = useState('todos');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const [verifyMessage, setVerifyMessage] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [paymentOverrides, setPaymentOverrides] = useState<Record<string, Partial<Colonia>>>({});
  const [detailInscricao, setDetailInscricao] = useState<Colonia | null>(null);
  const [editInscricao, setEditInscricao] = useState<Colonia | null>(null);
  const [editMessage, setEditMessage] = useState('');
  const [sendingWhatsappId, setSendingWhatsappId] = useState<string | null>(null);
  const [sentWhatsappId, setSentWhatsappId] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState('');
  const [comprasEvento, setComprasEvento] = useState<CompraEvento[]>([]);

  const availableYears = useMemo(() => {
    return getAvailableAdminPeriods(inscricoes);
  }, [inscricoes]);

  useEffect(() => {
    fetchComprasEvento()
      .then(setComprasEvento)
      .catch((erro) => console.warn('[Gerenciamento] Nao foi possivel carregar adicionais:', erro));
  }, []);

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const yearInscricoes = useMemo(
    () =>
      inscricoes
        .filter((inscricao) => getInscricaoYear(inscricao) === selectedYear && !deletedIds.includes(inscricao.id || ''))
        .map((inscricao) => ({ ...inscricao, ...(paymentOverrides[inscricao.id || ''] || {}) })),
    [deletedIds, inscricoes, paymentOverrides, selectedYear]
  );

  const turmas = useMemo(
    () =>
      Array.from(new Set(yearInscricoes.map((inscricao) => inscricao.turma || 'Sem turma'))).sort(compareTurmas),
    [yearInscricoes]
  );

  const filteredInscricoes = useMemo(() => {
    const search = normalizeText(searchTerm);
    const operation = normalizeText(operationTerm);

    return yearInscricoes
      .filter((inscricao) => {
        const searchable = normalizeText(
          [
            inscricao.nomeCompleto,
            inscricao.nomeMae,
            inscricao.telefoneMae,
            inscricao.id,
            inscricao.asaasPaymentId,
            inscricao.invoiceUrl
          ]
            .filter(Boolean)
            .join(' ')
        );
        const searchMatch = !search || searchable.includes(search);
        const operationMatch = !operation || searchable.includes(operation);
        const statusMatch = statusFilter === 'todos' || inscricao.status === statusFilter;
        const turmaMatch = turmaFilter === 'todos' || (inscricao.turma || 'Sem turma') === turmaFilter;
        return searchMatch && operationMatch && statusMatch && turmaMatch;
      })
      .sort((a, b) => a.nomeCompleto.localeCompare(b.nomeCompleto, 'pt-BR'));
  }, [operationTerm, searchTerm, statusFilter, turmaFilter, yearInscricoes]);

  const paidInscricoes = filteredInscricoes.filter((item) => item.pago);
  const pendingInscricoes = filteredInscricoes.filter((item) => !item.pago);
  const paidTotal = paidInscricoes.reduce((sum, item) => sum + item.valorTotal, 0);
  const pendingTotal = pendingInscricoes.reduce((sum, item) => sum + item.valorTotal, 0);
  const gratuitos = filteredInscricoes.filter((item) => item.gratuito).length;
  const adicionaisTotal = comprasEvento.filter((compra) => compra.pago).reduce((sum, compra) => sum + Number(compra.valorTotal || 0), 0);
  const totalGeral = paidTotal + adicionaisTotal;
  const filteredIds = filteredInscricoes.map((inscricao) => inscricao.id || '').filter(Boolean);
  const selectedVisibleIds = selectedIds.filter((id) => filteredIds.includes(id));
  const allVisibleSelected = filteredIds.length > 0 && selectedVisibleIds.length === filteredIds.length;

  const clearFilters = () => {
    setSearchTerm('');
    setOperationTerm('');
    setStatusFilter('todos');
    setTurmaFilter('todos');
  };

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((current) => checked ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id));
  };

  const toggleAllVisible = (checked: boolean) => {
    setSelectedIds((current) => checked ? Array.from(new Set([...current, ...filteredIds])) : current.filter((id) => !filteredIds.includes(id)));
  };

  const excluirInscricoes = async (ids: string[]) => {
    const validIds = ids.filter(Boolean);
    if (!validIds.length) return;
    const confirmation = validIds.length === 1
      ? 'Excluir esta inscrição definitivamente?'
      : `Excluir definitivamente ${validIds.length} inscrições selecionadas?`;
    if (!window.confirm(confirmation)) return;

    try {
      setDeleting(true);
      setDeleteError('');
      setDeleteMessage('');
      const totalDeleted = await deleteAdminInscricoes(validIds);
      setDeletedIds((current) => Array.from(new Set([...current, ...validIds])));
      setSelectedIds((current) => current.filter((id) => !validIds.includes(id)));
      setDeleteMessage(`${totalDeleted} inscrição(ões) excluída(s).`);
    } catch (requestError) {
      setDeleteError(requestError instanceof Error ? requestError.message : 'Erro ao excluir inscrições.');
    } finally {
      setDeleting(false);
    }
  };

  const enviarWhatsapp = async (inscricao: Colonia) => {
    const rowId = inscricao.id || '';
    if (!rowId || !inscricao.telefoneMae || sendingWhatsappId) return;

    try {
      setWhatsappError('');
      setSendingWhatsappId(rowId);
      await sendRegistrationConfirmation(rowId);
      setSentWhatsappId(rowId);
      window.setTimeout(() => setSentWhatsappId((current) => (current === rowId ? null : current)), 3000);
    } catch (requestError) {
      setWhatsappError(requestError instanceof Error ? requestError.message : 'Erro ao enviar mensagem no WhatsApp.');
    } finally {
      setSendingWhatsappId(null);
    }
  };

  const handleEditSaved = (updated: Colonia) => {
    if (updated.id) {
      setPaymentOverrides((current) => ({ ...current, [updated.id as string]: updated }));
    }
    setEditInscricao(null);
    setEditMessage(`Inscrição de ${updated.nomeCompleto} atualizada com sucesso.`);
  };

  const exportarExcel = async () => {
    setExporting(true);
    setExportProgress('Preparando...');
    try {
      await exportarInscricoesExcel(filteredInscricoes, selectedYear, (feitas, total) => {
        setExportProgress(`Fotos ${feitas}/${total}...`);
      });
    } catch (erro) {
      window.alert(erro instanceof Error ? erro.message : 'Não foi possível gerar a planilha.');
    } finally {
      setExporting(false);
      setExportProgress('');
    }
  };

  const verificarPagamentos = async () => {
    const idsToVerify = selectedVisibleIds.length ? selectedVisibleIds : filteredIds;
    if (!idsToVerify.length) return;

    try {
      setVerifying(true);
      setVerifyError('');
      setVerifyMessage('');
      const result = await verifyAdminPayments(idsToVerify);
      const overrides = result.results.reduce<Record<string, Partial<Colonia>>>((acc, item) => {
        if (!item.skipped) {
          acc[item.id] = {
            status: item.status,
            pago: item.pago,
            valorTotal: item.valorTotal,
            invoiceUrl: item.invoiceUrl,
            installmentCount: item.installmentCount,
            installmentValue: item.installmentValue
          };
        }
        return acc;
      }, {});
      setPaymentOverrides((current) => ({ ...current, ...overrides }));
      setVerifyMessage(`Verificação concluída: ${result.updated} atualizada(s), ${result.skipped} ignorada(s).`);
    } catch (requestError) {
      setVerifyError(requestError instanceof Error ? requestError.message : 'Erro ao verificar pagamentos no Asaas.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="legacy-admin">
      <AdminTopbar
        inscricoes={inscricoes}
        selectedYear={selectedYear}
        title="Gerenciamento - Colônia de Férias"
        onYearChange={setSelectedYear}
      />

      <main className="legacy-content">
        <section className="legacy-card">
          <div className="legacy-card-header">
            <h2>Inscrições</h2>
            <div className="legacy-card-filters">
              <input
                className="legacy-input search"
                placeholder="Pesquisar inscrições..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <select className="legacy-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="todos">Todos os status</option>
                <option value="confirmado">Confirmado</option>
                <option value="pendente">Pendente</option>
                <option value="cancelado">Cancelado</option>
              </select>
              <select className="legacy-select" value={turmaFilter} onChange={(event) => setTurmaFilter(event.target.value)}>
                <option value="todos">Todas as turmas</option>
                {turmas.map((turma) => (
                  <option key={turma} value={turma}>
                    {turma}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="legacy-toolbar">
            <div className="legacy-toolbar-controls">
              <div className="legacy-toolbar-filters">
                <label className="legacy-filter-field">
                  <span>Data inicial</span>
                  <input className="legacy-input date" placeholder="dd/mm/aaaa" />
                </label>
                <label className="legacy-filter-field">
                  <span>Data final</span>
                  <input className="legacy-input date" placeholder="dd/mm/aaaa" />
                </label>
                <label className="legacy-filter-field wide">
                  <span>Nº da operação</span>
                  <input
                    className="legacy-input operation"
                    placeholder="Informe o número da operação"
                    value={operationTerm}
                    onChange={(event) => setOperationTerm(event.target.value)}
                  />
                </label>
              </div>

              <div className="legacy-toolbar-actions">
                <button className="legacy-action-button red" type="button" onClick={clearFilters}>
                  <X size={15} />
                  Limpar
                </button>
                <button className="legacy-action-button green" type="button" onClick={() => window.print()}>
                  <Printer size={15} />
                  Imprimir
                </button>
                <button
                  className="legacy-action-button dark-green"
                  type="button"
                  onClick={() => void exportarExcel()}
                  disabled={exporting || filteredInscricoes.length === 0}
                >
                  <FileSpreadsheet size={15} />
                  {exporting ? exportProgress || 'Exportando...' : 'Excel'}
                </button>
                <button className="legacy-action-button orange" type="button" onClick={() => void verificarPagamentos()} disabled={verifying || filteredIds.length === 0}>
                  <CheckCircle size={15} />
                  {verifying ? 'Verificando...' : 'Verificar'}
                </button>
                <button
                  className="legacy-action-button red"
                  type="button"
                  onClick={() => void excluirInscricoes(selectedVisibleIds)}
                  disabled={deleting || selectedVisibleIds.length === 0}
                >
                  <Trash2 size={15} />
                  {deleting ? 'Excluindo...' : `Excluir selecionados (${selectedVisibleIds.length})`}
                </button>
              </div>
            </div>

            <div className="legacy-summary">
              <span>Quantidade: <strong>{filteredInscricoes.length}</strong></span>
              <span>Inscrições: <strong className="green-text">{formatCurrency(paidTotal)}</strong></span>
              <span>Pendente: <strong className="orange-text">{formatCurrency(pendingTotal)}</strong></span>
              <span>Gratuitas: <strong className="blue-text">{gratuitos}</strong></span>
              <span>Adicionais: <strong className="green-text">{formatCurrency(adicionaisTotal)}</strong></span>
              <span>Total: <strong>{formatCurrency(totalGeral)}</strong></span>
            </div>
          </div>
        </section>

        <section className="legacy-table-shell">
          {error && <div className="legacy-message error">{error}</div>}
          {deleteError && <div className="legacy-message error">{deleteError}</div>}
          {deleteMessage && <div className="legacy-message success">{deleteMessage}</div>}
          {verifyError && <div className="legacy-message error">{verifyError}</div>}
          {verifyMessage && <div className="legacy-message success">{verifyMessage}</div>}
          {editMessage && <div className="legacy-message success">{editMessage}</div>}
          {whatsappError && <div className="legacy-message error">{whatsappError}</div>}
          {loading && <div className="legacy-message">Carregando inscrições...</div>}
          {!loading && !error && (
            <table className="legacy-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={(event) => toggleAllVisible(event.target.checked)}
                      aria-label="Selecionar inscrições visíveis"
                    />
                  </th>
                  <th>Foto</th>
                  <th>Nome da Criança</th>
                  <th>Idade</th>
                  <th>Turma</th>
                  <th>Camisa</th>
                  <th>Responsável</th>
                  <th>WhatsApp</th>
                  <th>Status</th>
                  <th>Pago</th>
                  <th>Gratuito</th>
                  <th>Valor Total</th>
                  <th>Fatura</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredInscricoes.map((inscricao, index) => {
                  const rowId = inscricao.id || '';
                  return (
                  <tr key={rowId || `${inscricao.nomeCompleto}-${index}`}>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(rowId && selectedIds.includes(rowId))}
                        disabled={!rowId}
                        onChange={(event) => rowId && toggleSelected(rowId, event.target.checked)}
                        aria-label={`Selecionar ${inscricao.nomeCompleto}`}
                      />
                    </td>
                    <td><ChildPhoto inscricao={inscricao} /></td>
                    <td className="name-cell">{inscricao.nomeCompleto}</td>
                    <td>{inscricao.idade} anos</td>
                    <td>{inscricao.turma}</td>
                    <td>{inscricao.tamanhoCamisa}</td>
                    <td>{inscricao.nomeMae}</td>
                    <td>
                      <div className="legacy-whatsapp">
                        <span>{inscricao.telefoneMae}</span>
                        <button
                          type="button"
                          className={sentWhatsappId === rowId ? 'sent' : ''}
                          aria-label="Enviar confirmação por WhatsApp"
                          title="Enviar confirmação por WhatsApp"
                          disabled={!rowId || !inscricao.telefoneMae || sendingWhatsappId === rowId}
                          onClick={() => void enviarWhatsapp(inscricao)}
                        >
                          {sendingWhatsappId === rowId ? (
                            <LoaderCircle className="spin" size={15} />
                          ) : sentWhatsappId === rowId ? (
                            <CheckCircle size={15} />
                          ) : (
                            <MessageCircle size={15} />
                          )}
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className={`legacy-pill ${inscricao.status === 'pendente' ? 'warning' : 'success'}`}>
                        {(inscricao.status || 'confirmado').toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={`legacy-pill ${inscricao.pago ? 'info' : 'neutral'}`}>
                        {inscricao.pago ? 'PAGO' : 'NÃO'}
                      </span>
                    </td>
                    <td>
                      <span className="legacy-pill neutral">{inscricao.gratuito ? 'SIM' : 'NÃO'}</span>
                    </td>
                    <td className="legacy-price">{formatCurrency(inscricao.valorTotal)}</td>
                    <td>
                      {inscricao.invoiceUrl ? (
                        <a href={inscricao.invoiceUrl} target="_blank" rel="noreferrer" aria-label="Fatura">
                          <FileText size={16} />
                        </a>
                      ) : (
                        <FileText size={16} />
                      )}
                    </td>
                    <td>
                      <div className="legacy-row-actions">
                        <button type="button" aria-label="Informações" onClick={() => setDetailInscricao(inscricao)}><Info size={14} /></button>
                        <button type="button" aria-label="Editar" disabled={!rowId} onClick={() => setEditInscricao(inscricao)}><Edit3 size={14} /></button>
                        <button type="button" aria-label="Excluir" disabled={!rowId || deleting} onClick={() => void excluirInscricoes([rowId])}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </main>
      {detailInscricao && <RegistrationDetailsModal inscricao={detailInscricao} onClose={() => setDetailInscricao(null)} />}
      {editInscricao && (
        <RegistrationEditModal
          inscricao={editInscricao}
          onClose={() => setEditInscricao(null)}
          onSaved={handleEditSaved}
        />
      )}
    </div>
  );
};

export default GerenciamentoColonia;
